import { forwardRef, isValidElement, cloneElement } from "react";
import { Button } from "app/components/ui/Button/Button";
import { useFilterContext } from "../../contexts/FilterProvider";
import classNames from "classnames";
import PropTypes from "prop-types";
import FilterIcon from "app/features/product/productFilters/icons/FilterIcon";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { useIntersectionObserver } from "app/hooks";
import "./FilterTrigger.scss";

const composeHandlers = (userHandler, ourHandler) => {
	return function handleEvent(e) {
		if (typeof userHandler === "function") {
			userHandler(e);
		}

		if (e?.defaultPrevented) {
			return;
		}

		if (typeof e.isPropagationStopped === "function" && e.isPropagationStopped()) {
			return;
		}

		if (typeof ourHandler === "function") {
			ourHandler(e);
		}
	};
};

export const FilterTrigger = forwardRef(function FilterTrigger(
	{ asChild = false, className, children, onClick, ...props },
	ref
) {
	const { openDrawer, isOpen, selectedFiltersCount } = useFilterContext();

	const filterTriggerClassName = classNames("filter-trigger", {
		"filter-trigger--has-selected-filters": selectedFiltersCount > 0,
		[className]: className,
	});

	const handleClick = composeHandlers(onClick, openDrawer);

	const aria = { "aria-haspopup": "dialog", "aria-expanded": isOpen };

	if (asChild) {
		if (!isValidElement(children)) {
			return null;
		}

		return cloneElement(children, {
			className: classNames(children.props.className, filterTriggerClassName),
			onClick: composeHandlers(children.props.onClick, handleClick),
			ref,
			...props,
			...aria,
		});
	}

	return (
		<Button
			variant="borderless"
			onClick={handleClick}
			className={filterTriggerClassName}
			startIcon={<FilterIcon />}
			{...props}
			{...aria}
		>
			<FormattedMessage id="filter.mobile.button.label" />

			{selectedFiltersCount > 0 && ` (${selectedFiltersCount})`}
		</Button>
	);
});

export const StickyFilterButtonTrigger = ({ className, ...props }) => {
	const { selectedFiltersCount } = useFilterContext();
	const [sentinelRef, isIntersecting] = useIntersectionObserver({
		rootMargin: "1px 0px 0px 0px",
	});

	const buttonWrapperClassName = classNames("sticky-filter-button-trigger__wrapper", {
		"sticky-filter-button-trigger__wrapper--sticky": !isIntersecting,
		[className]: className,
	});

	const buttonClassName = classNames("sticky-filter-button-trigger", {
		"sticky-filter-button-trigger--has-selected-filters": selectedFiltersCount > 0,
	});

	return (
		<>
			<div ref={sentinelRef} />
			<section className={buttonWrapperClassName}>
				<Button startIcon={<FilterIcon />} className={buttonClassName} {...props}>
					<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
						<FormattedMessage id="filter.mobile.button.label" />

						{selectedFiltersCount > 0 && ` (${selectedFiltersCount})`}
					</Typography>
				</Button>
			</section>
		</>
	);
};

FilterTrigger.propTypes = {
	asChild: PropTypes.bool,
	className: PropTypes.string,
	children: PropTypes.node,
	onClick: PropTypes.func,
};
